/**
 * The user's appearance, as it is saved in the user preferences under
 * `appearance_v1`: the colour mode, up to three colours for the chrome, how
 * vivid they are and how much grain lies over them.
 */

import { isRecord } from '@/lib/is-record';

/** The user preference that holds the appearance. */
export const APPEARANCE_PREFERENCE = 'appearance_v1';

export const APPEARANCE_VERSION = 1;

/** The chrome takes at most this many colours, as a gradient across them. */
export const MAX_COLORS = 3;

export const DEFAULT_INTENSITY = 60;
export const DEFAULT_GRAIN = 0;

export type AppearanceMode = 'system' | 'light' | 'dark';

/** A colour picked on the field: hue 0 to 359, tone 0 (dark) to 100 (light). */
export interface AppearanceColor {
  hue: number;
  tone: number;
}

/** What tints the chrome, apart from the mode. */
export interface AppearanceLook {
  /** None keeps today's chrome. */
  colors: AppearanceColor[];
  /** 0 to 100: the colours' chroma as a share of the most the screen shows. */
  intensity: number;
  /** 0 to 100: the noise laid over the chrome. */
  grain: number;
}

export interface AppearanceV1 extends AppearanceLook {
  version: typeof APPEARANCE_VERSION;
  mode: AppearanceMode;
}

/** The chrome with nothing picked. */
export const DEFAULT_LOOK: AppearanceLook = {
  colors: [],
  intensity: DEFAULT_INTENSITY,
  grain: DEFAULT_GRAIN,
};

const MODES: readonly AppearanceMode[] = ['system', 'light', 'dark'];

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** A whole number from 0 to 100. */
export function percent(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

/** A whole hue from 0 to 359, going round past either end. */
export function wrapHue(value: number): number {
  const hue = Math.round(value) % 360;
  return hue < 0 ? hue + 360 : hue;
}

/** A colour with whole numbers in range. */
export function normalizeColor(color: AppearanceColor): AppearanceColor {
  return { hue: wrapHue(color.hue), tone: percent(color.tone) };
}

function parseColor(value: unknown): AppearanceColor | null {
  if (!isRecord(value)) return null;
  if (!isNumber(value.hue) || !isNumber(value.tone)) return null;
  return normalizeColor({ hue: value.hue, tone: value.tone });
}

/**
 * A saved appearance, or null for anything that is not one of this version:
 * numbers are rounded and brought into range, and colours past the third are
 * dropped.
 */
export function parseAppearance(value: unknown): AppearanceV1 | null {
  if (!isRecord(value) || value.version !== APPEARANCE_VERSION) return null;
  if (!MODES.includes(value.mode as AppearanceMode)) return null;
  if (!Array.isArray(value.colors)) return null;
  if (!isNumber(value.intensity) || !isNumber(value.grain)) return null;
  const colors: AppearanceColor[] = [];
  for (const entry of value.colors) {
    const color = parseColor(entry);
    if (!color) return null;
    if (colors.length < MAX_COLORS) colors.push(color);
  }
  return {
    version: APPEARANCE_VERSION,
    mode: value.mode as AppearanceMode,
    colors,
    intensity: percent(value.intensity),
    grain: percent(value.grain),
  };
}

/**
 * The appearance as it is sent: every field, every time. The server merges an
 * object one level into the saved one, so a field left out would keep an
 * older value.
 */
export function serializeAppearance(appearance: AppearanceV1): AppearanceV1 {
  return {
    version: APPEARANCE_VERSION,
    mode: appearance.mode,
    colors: appearance.colors.slice(0, MAX_COLORS).map(normalizeColor),
    intensity: percent(appearance.intensity),
    grain: percent(appearance.grain),
  };
}

/** The look of a saved appearance, or the default look with none. */
export function lookOf(appearance: AppearanceLook | null): AppearanceLook {
  if (!appearance) return DEFAULT_LOOK;
  return {
    colors: appearance.colors,
    intensity: appearance.intensity,
    grain: appearance.grain,
  };
}

export function looksEqual(a: AppearanceLook, b: AppearanceLook): boolean {
  return (
    a.intensity === b.intensity &&
    a.grain === b.grain &&
    a.colors.length === b.colors.length &&
    a.colors.every(
      (color, index) =>
        color.hue === b.colors[index].hue && color.tone === b.colors[index].tone
    )
  );
}

export function appearancesEqual(
  a: AppearanceV1 | null,
  b: AppearanceV1 | null
): boolean {
  if (a === null || b === null) return a === b;
  return a.mode === b.mode && looksEqual(a, b);
}
